import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// Import icons from our icon system
import { Kakao, Google, Apple } from '../../../assets/images';

// Import API modules
import { authorizeAPI, userAPI } from '../../../actions/imsServer';
import * as jwt from '../../../actions/jwt';
import { setAuth } from '../../../redux/infoSlice';
import { isWebView } from '../../../utils/base';
import appVersionManager from '../../../utils/appVersion';
import Popup from '../../popup';

// Styled components
const SocialButton = styled(Box)(({ theme }) => ({
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.figma.white[10],
    borderRadius: '60px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    '&[aria-disabled="true"]': {
        cursor: 'default',
        opacity: 0.5,
        pointerEvents: 'none',
    },
    '&:hover': {
        transform: 'scale(1.05)',
    },
}));

const SNS_SIGN_UP_STORAGE_KEY = 'snsData';
const SNS_SIGN_UP_STORAGE_EVENT = 'snsDataChanged';

const hasStoredValue = value =>
    value !== undefined && value !== null && value !== '';

const getStoredSnsData = () => {
    try {
        const stored = localStorage.getItem(SNS_SIGN_UP_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        return null;
    }
};

const isSameSnsAccount = (a, b) =>
    !!a?.snsType &&
    !!a?.snsId &&
    a.snsType === b?.snsType &&
    a.snsId === b?.snsId;

const mergeSnsData = (previous, next) => {
    if (!previous) return next || null;
    if (!next) return previous;

    return Array.from(new Set([...Object.keys(previous), ...Object.keys(next)]))
        .reduce((merged, key) => ({
            ...merged,
            [key]: hasStoredValue(next[key]) ? next[key] : previous[key],
        }), {});
};

const saveSnsSignUpData = snsData => {
    try {
        const storedSnsData = getStoredSnsData();
        const nextSnsData = isSameSnsAccount(storedSnsData, snsData)
            ? mergeSnsData(storedSnsData, snsData)
            : snsData;

        localStorage.setItem(SNS_SIGN_UP_STORAGE_KEY, JSON.stringify(nextSnsData));
        window.dispatchEvent(
            new CustomEvent(SNS_SIGN_UP_STORAGE_EVENT, { detail: nextSnsData })
        );
    } catch (error) {
        // localStorage 저장 실패 시에도 route state 없이 가입 화면으로 이동합니다.
    }
};

const SocialLoginProvider = ({
    display = isWebView() ? 'block' : 'none',
    // for test
    // display = 'block',
    // for test
    onLoginSuccess,
    onLoginError,
    showTitle = true,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');
    // ios / android: 앱에서 appVersionInfo로 전달된 플랫폼 (WebView가 아니면 null)
    const [platform, setPlatform] = useState(() => (isWebView() ? appVersionManager.getPlatform() : null));
    const [socialLoginPending, setSocialLoginPending] = useState(null);
    const socialLoginPendingRef = useRef(false);

    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    /** smart-diaper-admin signIn.js handleSNSLink와 동일: registerUser → SNS 토큰 재발급 → 로그인 완료 */
    const completeSnsLinkAndLogin = useCallback(
        async loginData => {
            const registerData = {
                snsType: loginData.snsType,
                snsId: loginData.snsId,
                loginId: loginData.email,
                name: loginData.name,
            };

            if (isWebView()) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'console',
                        data: [
                            '[SocialLoginProvider] completeSnsLinkAndLogin - registerData:',
                            registerData,
                        ],
                    })
                );
            }

            const response = await userAPI.registerUser(registerData);

            if (isWebView()) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'console',
                        data: [
                            '[SocialLoginProvider] completeSnsLinkAndLogin - registerUser response:',
                            response,
                        ],
                    })
                );
            }

            // signUp.js와 동일: 신규/연동 시 'created', 기타 성공 시 'ok' 등
            const registerOk =
                response.result === 'ok' || response.result === 'created';
            if (!registerOk) {
                if (isWebView()) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'console',
                            data: [
                                '[SocialLoginProvider] completeSnsLinkAndLogin - registerUser unexpected result:',
                                response,
                            ],
                        })
                    );
                }
                throw new Error('registerUser failed');
            }

            const pushToken = localStorage.getItem('pushToken') || '';

            if (isWebView()) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'console',
                        data: [
                            '[SocialLoginProvider] completeSnsLinkAndLogin - getToken request:',
                            {
                                grant_type: 'sns',
                                sns_type: loginData.snsType,
                                sns_id: loginData.snsId,
                                sns_code: loginData.snsCode || null,
                                push_token: pushToken,
                            },
                        ],
                    })
                );
            }

            const loginResponse = await authorizeAPI.getToken({
                grant_type: 'sns',
                sns_type: loginData.snsType,
                sns_id: loginData.snsId,
                sns_code: loginData.snsCode || null,
                push_token: pushToken,
            });

            if (isWebView()) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'console',
                        data: [
                            '[SocialLoginProvider] completeSnsLinkAndLogin - getToken response:',
                            loginResponse,
                        ],
                    })
                );
            }

            if (!loginResponse?.access_token) {
                if (isWebView()) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'console',
                            data: [
                                '[SocialLoginProvider] completeSnsLinkAndLogin - missing access_token:',
                                loginResponse,
                            ],
                        })
                    );
                }
                throw new Error('sns login retry failed - no access token');
            }

            const appType = localStorage.getItem('appType');
            const decoded = jwt.decode(loginResponse.access_token);

            if (appType === 'admin' && decoded.exp) {
                setPopupMessage(t('admin_only_access'));
                setPopupSubMessage('');
                setPopupOpen(true);
                return;
            }

            setPopupOpen(false);

            localStorage.setItem('accessToken', loginResponse.access_token);
            localStorage.setItem('refreshToken', loginResponse.refresh_token);

            const userResponse = await userAPI.getMyInfo(loginResponse.access_token);
            const userDataForStorage = userResponse.data.user;
            localStorage.setItem('meInfo', JSON.stringify(userDataForStorage));

            dispatch(
                setAuth({
                    accessToken: loginResponse.access_token,
                    refreshToken: loginResponse.refresh_token,
                })
            );

            window.dispatchEvent(new Event('meInfoChanged'));
            window.dispatchEvent(
                new CustomEvent('meInfoSynced', { detail: userDataForStorage })
            );

            if (isWebView()) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'accessToken',
                        data: {
                            accessToken: loginResponse.access_token,
                            refreshToken: loginResponse.refresh_token,
                        },
                    })
                );

                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'meInfo',
                        data: userDataForStorage,
                    })
                );

                try {
                    const meInfo = JSON.parse(localStorage.getItem('meInfo'));
                    if (meInfo) {
                        window.ReactNativeWebView.postMessage(
                            JSON.stringify({
                                type: 'subscribeTopics',
                            })
                        );
                    }
                } catch (e) {
                    console.error('Error setting up push notifications:', e);
                }
            }

            if (onLoginSuccess) {
                onLoginSuccess(loginResponse, userResponse);
            } else {
                navigate(`/?appType=${appType}`, { replace: true });
            }
        },
        [dispatch, navigate, onLoginSuccess, t]
    );

    // SNS 로그인 처리 함수
    const handleSNSLogin = useCallback(
        async loginData => {
            try {
                // pushToken을 localStorage에서 가져오기
                const pushToken = localStorage.getItem('pushToken') || '';

                // SNS 로그인을 위한 API 호출
                const response = await authorizeAPI.getToken({
                    grant_type: 'sns',
                    sns_type: loginData?.snsType, // SNS provider type (kakao, naver, google, apple)
                    sns_id: loginData?.snsId, // SNS id
                    sns_code: loginData?.snsCode || null,
                    push_token: pushToken,
                });

                if (response?.access_token) {
                    const appType = localStorage.getItem('appType');
                    const decoded = jwt.decode(response.access_token);

                    if (appType === 'admin' && decoded.exp) {
                        if (onLoginError) {
                            onLoginError(t('admin_only_access'));
                        }
                    } else {
                        localStorage.setItem(
                            'accessToken',
                            response.access_token
                        );
                        localStorage.setItem(
                            'refreshToken',
                            response.refresh_token
                        );

                        // meInfo를 먼저 로드·저장한 뒤 setAuth (App useEffect가 loadMeInfo 시 이미 localStorage에 meInfo 있도록)
                        const userResponse = await userAPI.getMyInfo(
                            response.access_token
                        );
                        const userDataForStorage = userResponse.data.user;
                        localStorage.setItem(
                            'meInfo',
                            JSON.stringify(userDataForStorage)
                        );

                        dispatch(
                            setAuth({
                                accessToken: response.access_token,
                                refreshToken: response.refresh_token,
                            })
                        );

                        window.dispatchEvent(new Event('meInfoChanged'));
                        // SNS 로그인 직후 메인 진입 시 "로딩 중..." 방지: App에 meInfo 즉시 전달
                        window.dispatchEvent(new CustomEvent('meInfoSynced', { detail: userDataForStorage }));

                        if (isWebView()) {
                            window.ReactNativeWebView.postMessage(
                                JSON.stringify({
                                    type: 'accessToken',
                                    data: { accessToken: response.access_token, refreshToken: response.refresh_token },
                                })
                            );
                            try {
                                const meInfo = JSON.parse(
                                    localStorage.getItem('meInfo')
                                );
                                if (meInfo) {
                                    window.ReactNativeWebView.postMessage(
                                        JSON.stringify({
                                            type: 'subscribeTopics',
                                        })
                                    );
                                }
                            } catch (e) {
                                console.error(
                                    'Error setting up push notifications:',
                                    e
                                );
                            }
                        }

                        if (onLoginSuccess) {
                            onLoginSuccess(response, userResponse);
                        } else {
                            navigate(`/?appType=${appType}`, { replace: true });
                        }
                    }
                }
            } catch (error) {
                if (onLoginError) {
                    onLoginError(error);
                } else {
                    // SNS 로그인 실패 시 기존 계정 확인
                    if (loginData?.email) {
                        try {
                            const checkResponse = await userAPI.checkUserExists({
                                loginId: loginData.email,
                            });

                            if (checkResponse.data.exists === true) {
                                // 기존 계정: 안내 후 확인 없이 registerUser → SNS 로그인 (smart-diaper-admin handleSNSLink와 동일)
                                showPopup(
                                    t(
                                        'an_account_already_exists_we_will_link_it_with_your_sns_account'
                                    )
                                );
                                try {
                                    await completeSnsLinkAndLogin(loginData);
                                } catch (linkError) {
                                    console.error('계정 연동 실패:', linkError);
                                    setPopupOpen(false);
                                    if (isWebView()) {
                                        window.ReactNativeWebView.postMessage(
                                            JSON.stringify({
                                                type: 'console',
                                                data: [
                                                    '[SocialLoginProvider] completeSnsLinkAndLogin error:',
                                                    linkError,
                                                    'error.response:',
                                                    linkError.response?.data,
                                                ],
                                            })
                                        );
                                    }
                                    if (onLoginError) {
                                        onLoginError(
                                            `${t('account_linking_failed')} ${t('try_again')}`
                                        );
                                    } else {
                                        showPopup(
                                            t('account_linking_failed'),
                                            t('try_again')
                                        );
                                    }
                                }
                            } else {
                                // 기존 계정이 없는 경우 회원가입 페이지로 이동
                                saveSnsSignUpData(loginData);
                                navigate('/auth/signup', { state: { step: 1 } });
                            }
                        } catch (checkError) {
                            console.error('계정 확인 실패:', checkError);
                            saveSnsSignUpData(loginData);
                            navigate('/auth/signup', { state: { step: 1 } });
                        }
                    } else {
                        // email이 없는 경우 회원가입 페이지로 이동
                        saveSnsSignUpData(loginData);
                        navigate('/auth/signup', { state: { step: 1 } });
                    }
                }
            }
        },
        [navigate, dispatch, t, onLoginSuccess, onLoginError, completeSnsLinkAndLogin]
    );

    // WebView 통신 설정
    useEffect(() => {
        // Notify Main.js that page is loaded
        if (isWebView()) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'pageLoaded',
                })
            );
        }

        // 앱 버전/플랫폼 정보 요청 (appVersionInfo 응답에 platform 포함)
        if (isWebView()) {
            appVersionManager.requestAppVersion?.();
            const p = appVersionManager.getPlatform();
            if (p) setPlatform(p);
        }

        const handleAppVersionUpdate = () => {
            const p = appVersionManager.getPlatform();
            if (p) setPlatform(p);
        };
        window.addEventListener('appVersionUpdated', handleAppVersionUpdate);

        // Listen for push token event
        const handlePushToken = event => {
            const token = event.detail;
            localStorage.setItem('pushToken', token);
        };

        // 전역 핸들러 함수 등록 (injectJavaScript에서 직접 호출)
        // 이 함수는 컴포넌트가 마운트될 때 등록되므로 injectJavaScript에서 호출 가능
        window.handleKakaoLoginResult = (messageData) => {
            if (messageData && messageData.success && messageData.data) {
                handleSNSLogin(messageData.data);
            }
        };

        // Listen for message events
        const handleMessage = event => {
            try {
                // Skip webpack hot reload messages and other non-JSON messages
                if (
                    typeof event.data === 'string' &&
                    (event.data.startsWith('webpackHot') ||
                        event.data.startsWith('webpack') ||
                        event.data.startsWith('{') === false)
                ) {
                    return;
                }

                const data =
                    typeof event.data === 'string'
                        ? JSON.parse(event.data)
                        : event.data;

                if (
                    data.type === 'kakaoLoginResult' ||
                    data.type === 'googleLoginResult' ||
                    data.type === 'appleLoginResult'
                ) {
                    socialLoginPendingRef.current = false;
                    setSocialLoginPending(null);

                    if (data.canceled) {
                        return;
                    }

                    if (data.success && data.data) {
                        // 카카오/구글/애플 로그인 성공 시 자동 로그인 처리
                        const loginData = data.data;

                        // sns 로그인 처리 함수 호출
                        handleSNSLogin(loginData);
                    } else {
                        // 카카오/구글/애플 로그인 실패
                        const errorMessage = data.error || t('sns_login_failed');
                        if (onLoginError) {
                            onLoginError(errorMessage);
                        } else {
                            console.error('SNS login failed:', errorMessage);
                        }
                    }
                }
            } catch (e) {
                // Only log errors for actual JSON parsing issues, not webpack messages
                if (
                    !event.data ||
                    typeof event.data !== 'string' ||
                    !event.data.startsWith('webpack')
                ) {
                    console.error('Error handling message:', e);
                }
            }
        };

        // Add event listeners
        window.addEventListener('pushTokenReceived', handlePushToken);
        window.addEventListener('message', handleMessage);

        // Cleanup
        return () => {
            window.removeEventListener('appVersionUpdated', handleAppVersionUpdate);
            window.removeEventListener('pushTokenReceived', handlePushToken);
            window.removeEventListener('message', handleMessage);
            if (window.handleKakaoLoginResult) {
                delete window.handleKakaoLoginResult;
            }
        };
    }, [handleSNSLogin, onLoginError, t]);

    const handleSocialLogin = provider => {
        const shouldLockButton = provider !== 'kakaoLogin';
        if (shouldLockButton && socialLoginPendingRef.current) return;

        if (shouldLockButton) {
            socialLoginPendingRef.current = true;
            setSocialLoginPending(provider);
        }
        if (isWebView()) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: provider })
            );
        } else if (shouldLockButton) {
            socialLoginPendingRef.current = false;
            setSocialLoginPending(null);
        }
    };

    // ios: 카카오/애플 | android(aos): 카카오/구글만
    const isIOS = platform === 'ios';
    const isAndroid = platform === 'android';
    // for test
    // const isIOS = true;
    // const isAndroid = true;
    // for test

    return (
        <Box sx={{ display: display }}>
            {/* Other Login Methods */}
            {showTitle && (
                <Typography
                    sx={{
                        color: theme.palette.figma.white[60],
                        fontSize: '14px',
                        fontWeight: 400,
                        letterSpacing: '-0.42px',
                        textAlign: 'center',
                        marginBottom: '20px',
                    }}
                >
                    {t('other_login_methods')}
                </Typography>
            )}

            {/* Social Login Buttons: iOS = 카카오/구글/애플, Android = 카카오/구글 */}
            <Box
                sx={{
                    display: 'flex',
                    gap: '40px',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <SocialButton
                    onClick={() => handleSocialLogin('kakaoLogin')}
                    aria-label="Kakao login"
                    aria-disabled={socialLoginPending ? 'true' : 'false'}
                >
                    <Kakao size={21} color={theme.palette.figma.white[100]} />
                </SocialButton>
                {isAndroid ? (
                    <SocialButton
                        onClick={() => handleSocialLogin('googleLogin')}
                        aria-label="Google login"
                        aria-disabled={socialLoginPending ? 'true' : 'false'}
                    >
                        <Google size={25} />
                    </SocialButton>
                ) : isIOS && (
                    <SocialButton
                        onClick={() => handleSocialLogin('appleLogin')}
                        aria-label="Apple login"
                        aria-disabled={socialLoginPending ? 'true' : 'false'}
                    >
                        <Apple size={30} color={theme.palette.figma.white[100]} />
                    </SocialButton>
                )}
            </Box>

            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />
        </Box>
    );
};

export default SocialLoginProvider;
